import React, { CSSProperties, FunctionComponent, ReactNode, SVGProps, useEffect, useState } from "react";
import { useWindowBreakpoints } from "../../custom-hooks/useWindowBreakpoints";

export enum ProdesegButtonEnumTypes {
    Primary = "primary",
    PrimaryLight = "primary-50",
    Secondary = "secondary",
    SecondaryVariant = "secondary-variant",
    Success = "success",
    Danger = "danger",
    Warning = "warning",
    Info = "info",
    Light = "light",
    Dark = "dark",
    Neutral = "neutral",
}

export enum ProdesegButtonEnumSizes {
    Small = "btn-sm",
    Normal = "",
    Large = "btn-lg",
}

export interface ProdesegButtonProps {
    /**
     * Tipo de botón que se necesita usar
     */
    type: ProdesegButtonEnumTypes;
    /**
     * Si el botón es de tipo outline
     */
    isOutline?: boolean;
    /**
     * Si el botón está desabilitado
     */
    isDisabled?: boolean;
    /**
     * Tamaño del botón
     */
    size?: ProdesegButtonEnumSizes;
    /**
     * Si el botón está conformado únicamente por un ícono
     */
    onlyIcon?: boolean;
    /**
     * Icono svg en forma de componente
     */
    Icon?: FunctionComponent<
        SVGProps<SVGSVGElement> & {
            title?: string;
        }
    >;
    /**
     * Si se desea ocultar el ícono de la izquierda
     */
    hideIconStart?: boolean;
    /**
     * Si se desea ocultar el ícono de la derecha
     */
    hideIconEnd?: boolean;
    /**
     * Clases bootstrap adicionales
     */
    className?: string;
    /**
     * Optional click handler
     */
    onClick?: () => void;

    /**
     * Titulo del botón
     */
    title?: string;

    style?: React.CSSProperties;

    children?: ReactNode;
}

type SchemeColors = {
    [key in ProdesegButtonEnumTypes]?: {
        className: string;
        style?: CSSProperties & Record<string, unknown>;
    };
};

export function ProdesegButton({
    Icon,
    className,
    onClick,
    style,
    children,
    hideIconStart = false,
    hideIconEnd = false,
    size = ProdesegButtonEnumSizes.Normal,
    type = ProdesegButtonEnumTypes.Primary,
    isOutline = false,
    onlyIcon = false,
    isDisabled,
    title = "",
}: Readonly<ProdesegButtonProps>): React.ReactElement {
    const [currentSize, setCurrentSize] = useState(size);
    const { isDownSm } = useWindowBreakpoints();

    useEffect(() => {
        const sizeToSet = isDownSm ? ProdesegButtonEnumSizes.Small : size;
        if (sizeToSet !== currentSize) setCurrentSize(sizeToSet);
    }, [isDownSm]);

    const getParamsBySize = (ps: string, pe: string, space: string, iconSize: string) => {
        return {
            style: {
                paddingLeft: ps,
                paddingRight: pe,
            },
            space,
            iconSize: `svg-${isDownSm && onlyIcon ? "m" : iconSize}`,
        };
    };

    const paramsBySize = {
        [ProdesegButtonEnumSizes.Small]: getParamsBySize("1rem", "3rem", "1", "xs"),
        [ProdesegButtonEnumSizes.Normal]: getParamsBySize("1.25rem", "4rem", "1", "m"),
        [ProdesegButtonEnumSizes.Large]: getParamsBySize("1.5rem", "6rem", "2", "l"),
    };

    const schemeColors: SchemeColors = {};
    Object.keys(ProdesegButtonEnumTypes).forEach(key => {
        const enumValue = ProdesegButtonEnumTypes[key as keyof typeof ProdesegButtonEnumTypes];
        schemeColors[enumValue] = {
            className: `btn${isOutline ? "-outline-" : "-"}${enumValue}`,
        };
    });

    const currentParamsBySize = paramsBySize[currentSize];

    // Secondary Variant
    schemeColors[ProdesegButtonEnumTypes.SecondaryVariant] = {
        className: `btn-${ProdesegButtonEnumTypes.Secondary} shadow-sm rounded-end-0 svg-${ProdesegButtonEnumTypes.Secondary}`,
        style: currentParamsBySize.style,
    };

    const currentSchemeColorByType = schemeColors[type];

    const IconComponent = Icon
        ? React.createElement(Icon, {
              className: currentParamsBySize.iconSize,
          })
        : null;

    return (
        <button
            type="button"
            className={`d-flex align-items-center fw-medium ${currentSize ?? ""} ${className ?? ""} 
            btn ${currentSchemeColorByType?.className ?? ""} p-${onlyIcon ? currentParamsBySize.space : "-"}`}
            style={{ ...currentSchemeColorByType?.style, ...style }}
            onClick={onClick}
            disabled={isDisabled}
            title={title}
        >
            {onlyIcon ? (
                IconComponent
            ) : (
                <>
                    {!hideIconStart && IconComponent}
                    <span
                        className={`mx-${currentParamsBySize.space} ${ProdesegButtonEnumSizes.Small === size ? "small" : ""}`}
                    >
                        {children}
                    </span>
                    {!hideIconEnd && IconComponent}
                </>
            )}
        </button>
    );
}
